import React, { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { PLANS, PLAN_IDS, SECTION_PLAN_MAP, canAccess, getPlan } from '../data/planConfig';
import {
  CheckCircle, Lock, RefreshCw, Zap, Crown, Shield, Gift, CreditCard,
  ArrowRight, Star, ChevronDown, ChevronUp, ExternalLink,
  LayoutGrid, BookOpen, Calendar, Users, MapPin, Heart,
  MessageCircle, BarChart3, Tag, Utensils, AlertTriangle,
  PartyPopper, Receipt, Send, TrendingUp, Award, Building2,
  UserCog, Globe, Plug
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const authHeader = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });

// Map section keys to display info
const SECTIONS_INFO = {
  dashboard:          { label: 'Dashboard', icon: LayoutGrid,      desc: 'Panel principal de operaciones' },
  libro:              { label: 'Libro Digital', icon: BookOpen,     desc: 'Agenda visual de reservaciones' },
  reservations:       { label: 'Reservaciones', icon: Calendar,     desc: 'Gestión completa de reservas' },
  waitlist:           { label: 'Lista de Espera', icon: Users,      desc: 'Cola digital de clientes' },
  customers:          { label: 'CRM Clientes', icon: Users,         desc: 'Base de datos de clientes' },
  'booking-page':     { label: 'Página Pública', icon: Globe,       desc: 'Página de reservas online' },
  'floor-plan':       { label: 'Plano de Sala', icon: MapPin,       desc: 'Distribución visual de mesas' },
  loyalty:            { label: 'Fidelización', icon: Heart,         desc: 'Programa de puntos y recompensas' },
  deposits:           { label: 'Depósitos', icon: CreditCard,       desc: 'Cobros anticipados y garantías' },
  'gift-cards':       { label: 'Tarjetas Regalo', icon: Gift,       desc: 'Venta de tarjetas de regalo' },
  whatsapp:           { label: 'WhatsApp Auto', icon: MessageCircle, desc: 'Mensajes automáticos' },
  analytics:          { label: 'Analytics', icon: BarChart3,        desc: 'Métricas y reportes' },
  'walk-in':          { label: 'Fila Walk-In', icon: Users,         desc: 'Cola digital sin reservación' },
  events:             { label: 'Eventos', icon: PartyPopper,        desc: 'Venta de tickets para eventos' },
  'allergen-menu':    { label: 'Menú Alérgenos', icon: Utensils,    desc: 'Menú con filtros de alérgenos' },
  'private-dining':   { label: 'Comedores Privados', icon: Crown,   desc: 'Salas exclusivas y eventos' },
  cfdi:               { label: 'Facturación CFDI', icon: Receipt,   desc: 'Facturas electrónicas SAT' },
  nps:                { label: 'Feedback & NPS', icon: Star,        desc: 'Encuestas de satisfacción' },
  marketing:          { label: 'Campañas', icon: Send,              desc: 'Email y WhatsApp marketing' },
  'risk-dashboard':   { label: 'Anti No-Shows', icon: AlertTriangle, desc: 'Predicción de ausencias con IA' },
  followup:           { label: 'Post-visita', icon: MessageCircle,  desc: 'Seguimiento automático' },
  benchmark:          { label: 'Benchmark', icon: TrendingUp,       desc: 'Comparativa con el mercado' },
  memberships:        { label: 'Membresías', icon: Award,           desc: 'Planes de suscripción propios' },
  corporate:          { label: 'Portal Corporativo', icon: Building2, desc: 'Cuentas empresariales' },
  personal:           { label: 'Personal / Turnos', icon: UserCog,  desc: 'Programación de empleados' },
  'acceso-garantizado': { label: 'Acceso Garantizado', icon: Shield, desc: 'Club VIP con mesa asegurada' },
  integrations:       { label: 'Integraciones', icon: Plug,         desc: 'Kapso, SendPulse y más' },
};

const PLAN_ICONS = { gratuito: Gift, basico: CreditCard, profesional: Zap, empresarial: Shield };
const PLAN_GRADIENTS = {
  gratuito:    'from-gray-400 to-gray-600',
  basico:      'from-blue-500 to-blue-700',
  profesional: 'from-orange-500 to-orange-700',
  empresarial: 'from-gray-800 to-black',
};

function PlanCard({ plan, currentPlanId, onUpgrade, upgrading }) {
  const [expanded, setExpanded] = useState(false);
  const isCurrent = currentPlanId === plan.id;
  const isDowngrade = PLANS.findIndex(p => p.id === plan.id) < PLANS.findIndex(p => p.id === currentPlanId);
  const PIcon = PLAN_ICONS[plan.id] || Zap;

  return (
    <div className={cn(
      'relative rounded-2xl overflow-hidden border-2 transition-all',
      isCurrent ? 'border-orange-500 shadow-xl shadow-orange-100 scale-[1.01]' : 'border-gray-200 hover:border-gray-300',
      plan.id === 'empresarial' && 'bg-gray-950 text-white border-gray-700'
    )}>
      {/* Popular Badge */}
      {plan.badge && !isCurrent && (
        <div className="absolute top-0 inset-x-0 text-center py-1 bg-orange-500 text-white text-xs font-bold z-10">
          ⭐ {plan.badge}
        </div>
      )}
      {isCurrent && (
        <div className="absolute top-0 inset-x-0 text-center py-1 bg-orange-500 text-white text-xs font-bold z-10">
          ✓ Tu plan actual
        </div>
      )}

      <div className={cn('p-6', (plan.badge || isCurrent) && 'pt-10')}>
        {/* Plan Header */}
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-xs font-bold bg-gradient-to-r mb-3 ${PLAN_GRADIENTS[plan.id]}`}>
          <PIcon className="w-3.5 h-3.5" />
          {plan.name}
        </div>

        <div className="mb-4">
          <p className={cn('text-3xl font-black', plan.id === 'empresarial' ? 'text-white' : 'text-gray-900')}>
            {plan.price_mxn === 0 ? 'Gratis' : `$${plan.price_mxn.toLocaleString()}`}
            {plan.price_mxn > 0 && <span className={cn('text-sm font-normal ml-1', plan.id === 'empresarial' ? 'text-gray-400' : 'text-gray-400')}>MXN/mes</span>}
          </p>
          <p className={cn('text-sm mt-1', plan.id === 'empresarial' ? 'text-gray-400' : 'text-gray-500')}>{plan.tagline}</p>
        </div>

        {/* Features */}
        <div className="space-y-2 mb-5">
          {plan.features.slice(0, expanded ? undefined : 5).map((f, i) => (
            <div key={i} className="flex items-start gap-2">
              <CheckCircle className={cn('w-4 h-4 flex-shrink-0 mt-0.5', plan.id === 'empresarial' ? 'text-orange-400' : 'text-emerald-500')} />
              <p className={cn('text-sm', plan.id === 'empresarial' ? 'text-gray-300' : 'text-gray-700')}>{f}</p>
            </div>
          ))}
        </div>

        {plan.features.length > 5 && (
          <button onClick={() => setExpanded(!expanded)} className={cn('text-xs font-medium flex items-center gap-1 mb-4', plan.id === 'empresarial' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600')}>
            {expanded ? <><ChevronUp className="w-3 h-3" /> Ver menos</> : <><ChevronDown className="w-3 h-3" /> Ver {plan.features.length - 5} características más</>}
          </button>
        )}

        {/* Limits */}
        <div className={cn('grid grid-cols-3 gap-2 mb-5 p-3 rounded-xl', plan.id === 'empresarial' ? 'bg-white/10' : 'bg-gray-50')}>
          {[
            { label: 'Reservaciones', value: plan.limits.reservations_per_month === -1 ? '∞' : plan.limits.reservations_per_month + '/mes' },
            { label: 'Usuarios', value: plan.limits.users === -1 ? '∞' : plan.limits.users },
            { label: 'Sucursales', value: plan.limits.locations === -1 ? '∞' : plan.limits.locations },
          ].map((l, i) => (
            <div key={i} className="text-center">
              <p className={cn('text-lg font-black', plan.id === 'empresarial' ? 'text-orange-400' : 'text-gray-900')}>{l.value}</p>
              <p className={cn('text-xs', plan.id === 'empresarial' ? 'text-gray-500' : 'text-gray-400')}>{l.label}</p>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        {isCurrent ? (
          <Button disabled className="w-full gap-2 border-orange-400 text-orange-600" variant="outline">
            <CheckCircle className="w-4 h-4" /> Plan Activo
          </Button>
        ) : isDowngrade ? (
          <Button variant="outline" className={cn('w-full', plan.id === 'empresarial' && 'border-gray-600 text-gray-300')} disabled>
            Plan inferior al actual
          </Button>
        ) : (
          <Button
            onClick={() => onUpgrade(plan.id)}
            disabled={upgrading === plan.id}
            className={cn('w-full gap-2', plan.btnClass)}
          >
            {upgrading === plan.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><ArrowRight className="w-4 h-4" /> {plan.price_mxn === 0 ? 'Comenzar gratis' : `Cambiar a ${plan.name}`}</>}
          </Button>
        )}
      </div>
    </div>
  );
}

function LockedFeatureCard({ section, requiredPlan, onUpgrade }) {
  const info = SECTIONS_INFO[section];
  if (!info) return null;
  const Icon = info.icon;
  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-100 rounded-xl group hover:border-orange-200 hover:bg-orange-50 transition-all">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
          <Lock className="w-4 h-4 text-gray-400" />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-500">{info.label}</p>
          <p className="text-xs text-gray-400">{info.desc}</p>
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => onUpgrade(requiredPlan.id)}
        className={cn('gap-1 text-xs flex-shrink-0 bg-gradient-to-r text-white', PLAN_GRADIENTS[requiredPlan.id])}
      >
        <ArrowRight className="w-3 h-3" />
        {requiredPlan.name}
      </Button>
    </div>
  );
}

export default function MisPlanes() {
  const [myPlan, setMyPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [showCompare, setShowCompare] = useState(false);

  const loadMyPlan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/stripe/my-plan`, { headers: authHeader() });
      if (res.ok) setMyPlan(await res.json());
      else setMyPlan({ plan_id: 'gratuito', plan_name: 'Gratuito', price_mxn: 0, status: 'active' });
    } catch {
      setMyPlan({ plan_id: 'gratuito', plan_name: 'Gratuito', price_mxn: 0, status: 'active' });
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadMyPlan(); }, [loadMyPlan]);

  const currentPlanId = myPlan?.plan_id || PLAN_IDS.FREE;
  const currentPlan = getPlan(currentPlanId);

  const handleUpgrade = async (planId) => {
    if (planId === currentPlanId) return;
    setUpgrading(planId);
    try {
      const res = await fetch(`${API_URL}/api/stripe/upgrade`, {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({ plan_id: planId, restaurant_id: 'demo' })
      });
      const data = await res.json();
      if (res.ok) {
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        } else {
          toast.success(`Plan actualizado a ${getPlan(planId).name}` + (data.demo_mode ? ' (modo demo)' : ''));
          loadMyPlan();
        }
      } else {
        toast.error(data.detail || 'Error al procesar el cambio de plan');
      }
    } catch { toast.error('Error de conexión'); }
    finally { setUpgrading(null); }
  };

  // Build locked sections list
  const lockedSections = Object.entries(SECTION_PLAN_MAP)
    .filter(([section, required]) => !canAccess(section, currentPlanId))
    .map(([section, required]) => ({ section, requiredPlan: getPlan(required) }))
    .sort((a, b) => {
      const order = [PLAN_IDS.FREE, PLAN_IDS.BASIC, PLAN_IDS.PRO, PLAN_IDS.ENT];
      return order.indexOf(a.requiredPlan.id) - order.indexOf(b.requiredPlan.id);
    });

  // Group locked by required plan
  const lockedByPlan = {};
  lockedSections.forEach(({ section, requiredPlan }) => {
    if (!lockedByPlan[requiredPlan.id]) lockedByPlan[requiredPlan.id] = { plan: requiredPlan, sections: [] };
    lockedByPlan[requiredPlan.id].sections.push(section);
  });

  const PIcon = PLAN_ICONS[currentPlanId] || Gift;

  if (loading) return (
    <div className="flex flex-col h-full overflow-hidden">
      <TopBar title="Mis Planes" />
      <div className="flex-1 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <TopBar title="Mis Planes" subtitle="Gestión de suscripción y funciones disponibles" />
      <div className="flex-1 overflow-y-auto p-6 space-y-8">

        {/* Current Plan Hero */}
        <div className={cn('rounded-2xl text-white p-6 bg-gradient-to-br relative overflow-hidden', PLAN_GRADIENTS[currentPlanId])}>
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/5" />
          <div className="absolute -right-4 -bottom-4 w-24 h-24 rounded-full bg-white/5" />
          <div className="relative">
            <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
              <div>
                <p className="text-white/60 text-sm font-medium uppercase tracking-wider mb-1">Tu plan actual</p>
                <div className="flex items-center gap-2">
                  <PIcon className="w-8 h-8" />
                  <h2 className="text-3xl font-black">{currentPlan.name}</h2>
                </div>
                <p className="text-white/70 mt-1">{currentPlan.tagline}</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-black">{currentPlan.price_mxn === 0 ? 'Gratis' : `$${currentPlan.price_mxn.toLocaleString()}`}</p>
                {currentPlan.price_mxn > 0 && <p className="text-white/60 text-sm">MXN por mes</p>}
                <Badge className="mt-2 bg-white/20 text-white border-0">
                  {myPlan?.is_trial ? `Prueba: ${myPlan.trial_ends_at}` : myPlan?.status === 'active' ? 'Activo' : myPlan?.status}
                </Badge>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-2">
              {[
                { label: 'Reservaciones', value: currentPlan.limits.reservations_per_month === -1 ? 'Ilimitadas' : `${currentPlan.limits.reservations_per_month}/mes` },
                { label: 'Usuarios', value: currentPlan.limits.users === -1 ? 'Ilimitados' : currentPlan.limits.users },
                { label: 'Sucursales', value: currentPlan.limits.locations === -1 ? 'Ilimitadas' : currentPlan.limits.locations },
              ].map((l, i) => (
                <div key={i} className="bg-white/10 rounded-xl p-3 text-center">
                  <p className="text-xl font-black">{l.value}</p>
                  <p className="text-xs text-white/60">{l.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Locked Features */}
        {lockedSections.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-black text-gray-900">Funciones Bloqueadas</h2>
                <p className="text-sm text-gray-500">Actualiza tu plan para desbloquear estas secciones</p>
              </div>
              <Badge className="bg-orange-100 text-orange-700">{lockedSections.length} funciones bloqueadas</Badge>
            </div>
            <div className="space-y-6">
              {Object.values(lockedByPlan).map(({ plan, sections }) => (
                <div key={plan.id}>
                  <div className={cn('flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-sm font-bold bg-gradient-to-r w-fit mb-3', PLAN_GRADIENTS[plan.id])}>
                    {React.createElement(PLAN_ICONS[plan.id] || Zap, { className: 'w-4 h-4' })}
                    Requiere plan {plan.name} — ${plan.price_mxn.toLocaleString()} MXN/mes
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {sections.map(s => (
                      <LockedFeatureCard key={s} section={s} requiredPlan={plan} onUpgrade={handleUpgrade} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Plans Comparison */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-black text-gray-900">Comparar Todos los Planes</h2>
              <p className="text-sm text-gray-500">Elige el plan ideal para el tamaño y necesidades de tu restaurante</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
            {PLANS.map(plan => (
              <PlanCard
                key={plan.id}
                plan={plan}
                currentPlanId={currentPlanId}
                onUpgrade={handleUpgrade}
                upgrading={upgrading}
              />
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="bg-gray-50 rounded-2xl p-6">
          <h3 className="font-black text-lg text-gray-900 mb-4">Preguntas Frecuentes</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { q: '¿Puedo cambiar de plan en cualquier momento?', a: 'Sí. Los upgrades son inmediatos y solo pagas la diferencia prorrateada. Los downgrades aplican al siguiente ciclo.' },
              { q: '¿Hay contratos de permanencia?', a: 'No. Todos los planes son mes a mes. Cancela cuando quieras desde esta sección sin penalidad.' },
              { q: '¿Qué pasa si llego al límite de reservaciones?', a: 'En el plan Gratuito (50/mes), al alcanzar el límite se bloquean nuevas reservaciones hasta el siguiente mes o hasta que cambies de plan.' },
              { q: '¿Los pagos son seguros?', a: 'Sí. Los pagos se procesan 100% a través de Stripe, el estándar mundial en seguridad de pagos. OkReserva no almacena datos de tarjeta.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-4 border border-gray-100">
                <p className="font-semibold text-gray-900 text-sm mb-2">{item.q}</p>
                <p className="text-sm text-gray-500">{item.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Support CTA */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 text-white flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="font-black text-lg">¿Necesitas un plan personalizado?</h3>
            <p className="text-white/80 text-sm mt-1">Si tienes una cadena de restaurantes o necesidades especiales, podemos crear un plan a medida para ti.</p>
          </div>
          <Button className="bg-white text-orange-600 hover:bg-orange-50 gap-2 font-bold">
            <MessageCircle className="w-4 h-4" /> Contactar ventas
          </Button>
        </div>
      </div>
    </div>
  );
}

